import RAPIER from '@dimforge/rapier3d-compat';
import { CELL } from '../core/config';
import type { Box, Vec } from '../destruction/structure';

export interface OccupiedCollider {
  key: string;
  boxes: Box[];
}

/** Spatially bounded compounds preserve every exact cuboid and empty gap while
 * avoiding a separate broad-phase proxy for each facade panel and slab. */
export function occupiedColliderGroups(boxes: Box[]): OccupiedCollider[] {
  const tiles = new Map<string, Box[]>();
  for (const b of boxes) {
    const tile = `${Math.floor(b.x / 8)},${Math.floor(b.y / 8)},${Math.floor(b.z / 8)}`;
    let list = tiles.get(tile);
    if (!list) tiles.set(tile, (list = []));
    list.push(b);
  }
  const groups: OccupiedCollider[] = [];
  for (const [tile, list] of tiles)
    for (let offset = 0; offset < list.length; offset += 64) {
      const parts = list.slice(offset, offset + 64);
      groups.push({
        key: `${tile}:${parts.map((b) => `${b.x},${b.y},${b.z},${b.w},${b.h},${b.d}`).join(';')}`,
        boxes: parts,
      });
    }
  return groups;
}

export function occupiedColliderDesc(group: OccupiedCollider, origin: Vec): RAPIER.ColliderDesc {
  const first = group.boxes[0],
    centre = {
      x: (first.x + first.w / 2) * CELL,
      y: (first.y + first.h / 2) * CELL,
      z: (first.z + first.d / 2) * CELL,
    };
  const desc =
    group.boxes.length === 1
      ? RAPIER.ColliderDesc.cuboid((first.w * CELL) / 2, (first.h * CELL) / 2, (first.d * CELL) / 2)
      : RAPIER.ColliderDesc.compound(
          group.boxes.map((b) => new RAPIER.Cuboid((b.w * CELL) / 2, (b.h * CELL) / 2, (b.d * CELL) / 2)),
          group.boxes.map((b) => ({
            x: (b.x + b.w / 2) * CELL - centre.x,
            y: (b.y + b.h / 2) * CELL - centre.y,
            z: (b.z + b.d / 2) * CELL - centre.z,
          })),
          group.boxes.map(() => ({ x: 0, y: 0, z: 0, w: 1 })),
        );
  return desc.setTranslation(centre.x - origin.x, centre.y - origin.y, centre.z - origin.z);
}
